import { useMaybeRoomContext } from '@livekit/components-react';
import { useCallback, useEffect, useMemo } from 'react';
import { RoomEvent, type RoomEventCallbacks } from 'livekit-client';
import { type TutorBotDataReceivedCallback } from '../TutorBotVoiceAgent.types';
import { parseMessage } from '../utils';

export const useRoomEventListener = <Type extends RoomEvent>(type: Type, ...listeners: RoomEventCallbacks[Type][]) => {
    const room = useMaybeRoomContext();

    const handler = useCallback(
        (...params: Parameters<RoomEventCallbacks[Type]>) => {
            // @ts-expect-error TS is not able to infer the correct type for the listener, but the type of the
            // parent hook infers the correct type.
            listeners.forEach(listener => listener(...params));
        },
        [listeners],
    ) as RoomEventCallbacks[Type];

    useEffect(() => {
        if (!room) return undefined;
        room.on(type, handler);

        return () => {
            room.off(type, handler);
        };
    }, [room, handler, type]);
};

/*
    Wraps useRoomEventListener in order to do some modifications to the params before triggering
    data recieved callbacks:
    * parses message into an object
    * adds conversationId
*/
export const useDataReceivedEventListener = ({
    conversationId,
    listeners,
}: {
    conversationId: string;
    listeners: (TutorBotDataReceivedCallback | null | undefined)[];
}) => {
    const nativeListeners: RoomEventCallbacks[RoomEvent.DataReceived][] = useMemo(
        () =>
            listeners.filter(Boolean).map(listener =>
                getDataReceivedCallback(
                    // casting here is safe because the type on the function ensured that when
                    // the type is DataReceived, all the listners are TutorBotRoomEventCallbacks
                    listener as unknown as TutorBotDataReceivedCallback,
                    conversationId,
                ),
            ) as unknown as RoomEventCallbacks[RoomEvent.DataReceived][],
        [listeners, conversationId],
    );

    return useRoomEventListener(RoomEvent.DataReceived, ...nativeListeners);
};

function getDataReceivedCallback(callback: TutorBotDataReceivedCallback, conversationId: string) {
    const wrapper = (...params: Parameters<RoomEventCallbacks[RoomEvent.DataReceived]>) => {
        const [message, participant, kind, topic] = params;
        const dataMessage = parseMessage(message);
        callback({ message: dataMessage, participant, kind, topic, conversationId });
    };
    return wrapper;
}
